import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import styles from './styles.module.scss';
import { ConsentState, FormData } from './types';
import OptionalConsents, {
  DEFAULT_OPTIONAL_CONSENTS,
  OptionalConsentAnswers,
  areAllConsentsAnswered,
} from '../shared/consent/OptionalConsents';
import { useZipCodeCheck } from './hooks/useZipCodeCheck';
import { submitLead } from './utils/api';
import {
  trackSlideAnswer,
  trackSliderSuccess,
  trackSliderFailure,
  trackLeadCreated,
  trackEnpalSourceCookie,
} from './utils/tracking';
import { getUTMParams, splitFullName } from './utils/helpers';
import { OrganicIcons, renderIcon } from './components/OrganicIcons';
import {
  PRODUCT_OPTS,
  HOME_OPTS,
  TIMELINE_OPTS,
  SPEND_OPTS,
  SPEND_CALLOUT,
  SEARCH_TASKS,
  INCENTIVES,
  FORM_STEPS,
  labelFor,
  mapToFormData,
  OrganicOption,
} from './config/organicOptions';

type Phase = 'cap' | 'searching' | 'results' | 'form';

interface OrganicFields {
  product: string;
  home: string;
  timeline: string;
  spend: string;
  cap: string;
  name: string;
  email: string;
  phone: string;
}

const EMPTY: OrganicFields = {
  product: '',
  home: '',
  timeline: '',
  spend: '',
  cap: '',
  name: '',
  email: '',
  phone: '',
};

const validators = {
  cap: (v: string) => /^\d{5}$/.test(v.trim()),
  name: (v: string) => v.trim().length >= 2,
  email: (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim()),
  phone: (v: string) => v.replace(/[^\d]/g, '').length >= 9,
};

const ERR = {
  cap: 'Inserisci un CAP valido (5 cifre).',
  name: 'Inserisci nome e cognome.',
  email: "Controlla l'indirizzo email.",
  phone: 'Inserisci un numero di almeno 9 cifre.',
};

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
  error?: string;
  type?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  placeholder?: string;
  autoComplete?: string;
}

const Field: React.FC<FieldProps> = ({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  type = 'text',
  inputMode,
  placeholder,
  autoComplete,
}) => (
  <div className={styles.field}>
    <label htmlFor={id}>
      {label} <span className={styles.req}>*</span>
    </label>
    <input
      id={id}
      className={`${styles.input} ${error ? styles.inputErr : ''}`}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      type={type}
      inputMode={inputMode}
      placeholder={placeholder}
      autoComplete={autoComplete}
      aria-invalid={!!error}
      aria-describedby={error ? `${id}-e` : undefined}
    />
    {error && (
      <div className={styles.errmsg} id={`${id}-e`} role="alert">
        <OrganicIcons.help /> {error}
      </div>
    )}
  </div>
);

const SliderOrganic: React.FC = () => {
  const [phase, setPhase] = useState<Phase>('cap');
  const [step, setStep] = useState(0);
  const [search, setSearch] = useState(0);
  const [fields, setFields] = useState<OrganicFields>(EMPTY);
  const [dir, setDir] = useState<'next' | 'prev'>('next');
  const [anim, setAnim] = useState(true);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const { isValid: isCapValid, capFeedback, checkZip } = useZipCodeCheck();

  const [optionalConsents, setOptionalConsents] = useState<OptionalConsentAnswers>(
    DEFAULT_OPTIONAL_CONSENTS
  );

  const consentForApi: ConsentState = useMemo(
    () => ({
      enpalMarketingConsent: optionalConsents.enpalMarketingConsent === true,
      thirdPartyMarketing: optionalConsents.thirdPartyMarketing === true,
      thirdPartyProfiling: optionalConsents.thirdPartyProfiling === true,
    }),
    [optionalConsents]
  );

  const consentsComplete = areAllConsentsAnswered(optionalConsents);

  useEffect(() => {
    const utmParams = getUTMParams();
    trackEnpalSourceCookie(utmParams.utm_source || 'organic');
  }, []);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, [phase, step, done]);

  useEffect(() => {
    setAnim(true);
    const t = setTimeout(() => setAnim(false), 360);
    return () => clearTimeout(t);
  }, [phase, step]);

  useEffect(() => {
    if (phase !== 'searching') return;
    setSearch(0);
    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      setSearch(i);
      if (i >= SEARCH_TASKS.length) {
        window.clearInterval(id);
        window.setTimeout(() => {
          setDir('next');
          setPhase('results');
        }, 550);
      }
    }, 680);
    return () => window.clearInterval(id);
  }, [phase]);

  const setField = (key: keyof OrganicFields, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
    if (key === 'cap') {
      checkZip(value);
    }
  };

  const goNext = useCallback(() => {
    setDir('next');
    setStep((s) => Math.min(s + 1, FORM_STEPS - 1));
  }, []);

  const markTouched = (k: string) => setTouched((p) => ({ ...p, [k]: true }));

  const errOf = (k: keyof typeof validators) => {
    const show = touched[k] || submitted;
    if (!show) return '';
    return validators[k](fields[k] || '') ? '' : ERR[k];
  };

  const addrValid = validators.cap(fields.cap || '');
  const contactValid = (['name', 'email', 'phone'] as const).every((k) =>
    validators[k](fields[k] || '')
  );

  const bonusCount = capFeedback?.bonusCount ?? INCENTIVES.length;

  const startSearch = () => {
    setSubmitted(true);
    if (!addrValid || isCapValid === false) return;
    setSubmitted(false);
    setDir('next');
    setPhase('searching');
    trackSlideAnswer('location_form', 'CAP hero', fields.cap);
  };

  const goToForm = () => {
    setDir('next');
    setPhase('form');
    setStep(0);
  };

  const pick = (key: keyof OrganicFields, val: string, slideId: string, title: string) => {
    setField(key, val);
    trackSlideAnswer(slideId, title, val);
    if (step < FORM_STEPS - 1) {
      window.setTimeout(goNext, 280);
    }
  };

  const submitContacts = async () => {
    setSubmitted(true);
    if (!contactValid) return;

    setLoading(true);
    const uid = uuidv4();
    const formData = mapToFormData(fields) as FormData;

    try {
      const id = await submitLead(formData, uid, consentForApi);
      if (id) {
        trackSliderSuccess();
        if (formData.email) {
          const { firstName, lastName } = splitFullName(formData.name || '');
          window.setTimeout(() => {
            trackLeadCreated(
              formData.email!,
              formData.phone || '',
              formData.zip || '',
              firstName,
              lastName
            );
          }, 120);
        }
        setDir('next');
        setDone(true);
      } else {
        trackSliderFailure();
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      trackSliderFailure();
    } finally {
      setLoading(false);
    }
  };

  const optList = (
    opts: OrganicOption[],
    key: keyof OrganicFields,
    slideId: string,
    title: string,
    noAdvance?: boolean
  ) => (
    <div className={styles.opts} role="radiogroup" aria-label="Opzioni">
      {opts.map((o) => {
        const sel = fields[key] === o.v;
        return (
          <button
            key={o.v}
            type="button"
            className={`${styles.opt} ${sel ? styles.optSelected : ''}`}
            role="radio"
            aria-checked={sel}
            onClick={() =>
              noAdvance ? setField(key, o.v) : pick(key, o.v, slideId, title)
            }
          >
            <span className={styles.ico}>{renderIcon(o.ico)}</span>
            <span className={styles.lab}>
              <b>{o.t}</b>
              <small>{o.s}</small>
            </span>
            {sel ? (
              <span className={styles.tick}>
                <OrganicIcons.check />
              </span>
            ) : (
              <span className={styles.chev}>
                <OrganicIcons.chev />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );

  if (done) {
    const spendLabel = fields.spend
      ? fields.spend.replace('<', 'fino a ').replace('>', 'oltre ').replace('-', ' – ') + ' €'
      : '—';

    return (
      <section className={styles.sliderSection}>
        <div className={`${styles.slider} ${styles.isSuccess}`} role="region" aria-label="Richiesta inviata">
          <div className={styles.success}>
            <div className={styles.seal}>
              <OrganicIcons.check s={34} />
            </div>
            <h2 ref={headingRef} tabIndex={-1} className={styles.successTitle} style={{ outline: 'none' }}>
              Richiesta inviata! 🎉
            </h2>
            <p className={styles.lead}>
              Stiamo selezionando gli incentivi migliori a cui puoi accedere per risparmiare!
            </p>
            <div className={styles.recap}>
              <div className={styles.rrow}>
                <span>Bonus individuati</span>
                <b>
                  {bonusCount} nel CAP {fields.cap || '—'}
                </b>
              </div>
              <div className={styles.rrow}>
                <span>Prodotto</span>
                <b>{labelFor(PRODUCT_OPTS, fields.product) || '—'}</b>
              </div>
              <div className={styles.rrow}>
                <span>Abitazione</span>
                <b>{labelFor(HOME_OPTS, fields.home) || '—'}</b>
              </div>
              <div className={styles.rrow}>
                <span>Spesa attuale</span>
                <b>{spendLabel}</b>
              </div>
              <div className={styles.rrow}>
                <span>Installazione</span>
                <b>{labelFor(TIMELINE_OPTS, fields.timeline) || '—'}</b>
              </div>
              <div className={styles.rrow}>
                <span>Zona (CAP)</span>
                <b>{fields.cap || '—'}</b>
              </div>
              <div className={styles.rrow}>
                <span>Contatto</span>
                <b>{fields.name || '—'}</b>
              </div>
            </div>
            <div className={styles.badge}>
              <OrganicIcons.clock /> Nessun anticipo · Consulenza gratuita entro 24H
            </div>
          </div>
        </div>
      </section>
    );
  }

  let body: React.ReactNode;
  if (step === 0) {
    body = (
      <>
        <span className={styles.eyebrow}>
          <OrganicIcons.check s={13} /> {bonusCount} bonus pronti per te nel CAP {fields.cap}
        </span>
        <h2 className={styles.qTitle} ref={headingRef} tabIndex={-1}>
          A quale prodotto sei interessato?
        </h2>
        <p className={styles.qSub} />
        {optList(PRODUCT_OPTS, 'product', 'customer_type', 'A quale prodotto sei interessato?')}
      </>
    );
  } else if (step === 1) {
    body = (
      <>
        <h2 className={styles.qTitle} ref={headingRef} tabIndex={-1}>
          Che tipo di abitazione possiedi?
        </h2>
        <p className={styles.qSub}>Ci aiuta a stimare la soluzione più adatta a te.</p>
        {optList(HOME_OPTS, 'home', 'house_type', 'Che tipo di abitazione possiedi?')}
      </>
    );
  } else if (step === 2) {
    body = (
      <>
        <h2 className={styles.qTitle} ref={headingRef} tabIndex={-1}>
          Entro quando vorresti installare?
        </h2>
        <p className={styles.qSub}>
          Alcuni bonus hanno scadenza limitata: sapere i tempi ci aiuta a darti la priorità giusta.
        </p>
        {optList(TIMELINE_OPTS, 'timeline', 'install_timeline', 'Entro quando vorresti installare?')}
      </>
    );
  } else if (step === 3) {
    body = (
      <>
        <h2 className={styles.qTitle} ref={headingRef} tabIndex={-1}>
          Quanto spendi di luce al mese?
        </h2>
        <p className={styles.qSub}>Una stima va benissimo — scopri subito quanto potresti risparmiare.</p>
        {optList(SPEND_OPTS, 'spend', 'monthly_spend', 'Quanto spendi di luce al mese?', true)}
        {fields.spend && SPEND_CALLOUT[fields.spend] && (
          <div className={styles.callout} role="status" key={fields.spend}>
            <span className={styles.cbadge}>
              <OrganicIcons.euro />
            </span>
            <span>
              Fino a <b className={styles.saveamt}>{SPEND_CALLOUT[fields.spend].amount}</b>{' '}
              {SPEND_CALLOUT[fields.spend].text}
            </span>
          </div>
        )}
        {fields.spend && (
          <button type="button" className={styles.cta} onClick={goNext} style={{ marginTop: 14 }}>
            Continua{' '}
            <span className={styles.arrow}>
              <OrganicIcons.arrow />
            </span>
          </button>
        )}
      </>
    );
  } else {
    body = (
      <>
        <h2 className={styles.qTitle} ref={headingRef} tabIndex={-1}>
          Ultimo step:
        </h2>
        <p className={styles.qSub}>
          Lascia i tuoi contatti per essere ricontattato entro 24h da un nostro consulente per un preventivo
          gratuito e senza impegno.
        </p>
        <Field
          id="name"
          label="Nome e cognome"
          value={fields.name}
          placeholder="Mario Rossi"
          autoComplete="name"
          onChange={(e) => setField('name', e.target.value)}
          onBlur={() => markTouched('name')}
          error={errOf('name')}
        />
        <Field
          id="email"
          label="Email"
          value={fields.email}
          type="email"
          inputMode="email"
          placeholder="mario.rossi@email.it"
          autoComplete="email"
          onChange={(e) => setField('email', e.target.value)}
          onBlur={() => markTouched('email')}
          error={errOf('email')}
        />
        <Field
          id="phone"
          label="Telefono"
          value={fields.phone}
          type="tel"
          inputMode="tel"
          placeholder="333 1234567"
          autoComplete="tel"
          onChange={(e) => setField('phone', e.target.value)}
          onBlur={() => markTouched('phone')}
          error={errOf('phone')}
        />
        <button type="button" className={styles.cta} onClick={submitContacts} disabled={loading}>
          {loading ? (
            <>
              <span className={styles.spin} /> Invio in corso…
            </>
          ) : (
            <>
              Richiedi i miei bonus{' '}
              <span className={styles.arrow}>
                <OrganicIcons.arrow />
              </span>
            </>
          )}
        </button>
        <p className={styles.footLegal}>
          Trattiamo i vostri dati di contatto su vostra richiesta allo scopo di elaborare
          un'offerta gratuita e non vincolante. Troverete ulteriori informazioni nella
          nostra{' '}
          <a
            href="https://www.enpal.com/it/protezione-dei-dati"
            target="_blank"
            rel="noopener noreferrer"
          >
            informativa sul trattamento dei dati
          </a>
          , del cui contenuto avete preso atto.
        </p>
        <OptionalConsents
          answers={optionalConsents}
          onChange={setOptionalConsents}
        />
        {!consentsComplete && (
          <p className={styles.consentHelper}>
            Se non rispondi, i consensi facoltativi verranno registrati come
            « No ».
          </p>
        )}
      </>
    );
  }

  let content: React.ReactNode;
  if (phase === 'cap') {
    content = (
      <div className={styles.capHero} tabIndex={-1} style={{ outline: 'none' }}>
        <div className={styles.capRow}>
          <input
            className={`${styles.capInline} ${submitted && !addrValid ? styles.capInlineErr : ''}`}
            value={fields.cap}
            inputMode="numeric"
            maxLength={5}
            placeholder="Inserisci il CAP e scopri gli incentivi"
            aria-label="Inserisci il tuo CAP e scopri gli incentivi disponibili"
            autoComplete="postal-code"
            onChange={(e) => setField('cap', e.target.value.replace(/[^\d]/g, ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') startSearch();
            }}
          />
          <button type="button" className={styles.capGo} onClick={startSearch} aria-label="Scopri i bonus">
            <OrganicIcons.arrow />
          </button>
        </div>
        {submitted && !addrValid && (
          <div className={styles.errmsg} role="alert">
            <OrganicIcons.help /> {ERR.cap}
          </div>
        )}
        <div className={styles.capTrust}>
          <span>
            <OrganicIcons.users /> 100.000+ clienti in Italia
          </span>
          <span>
            <OrganicIcons.shield /> 0€ anticipo
          </span>
          <span>
            <OrganicIcons.card /> Finanziamento su misura
          </span>
        </div>
      </div>
    );
  } else if (phase === 'searching') {
    content = (
      <div className={styles.search}>
        <div className={styles.ring} />
        <h2 ref={headingRef} tabIndex={-1} className={styles.searchTitle} style={{ outline: 'none' }}>
          Cerco i bonus per te…
        </h2>
        <p className={styles.sub}>Sto analizzando gli incentivi attivi nel CAP {fields.cap}</p>
        <div className={styles.tasks}>
          {SEARCH_TASKS.map((t, i) => {
            const state =
              i < search ? styles.taskDone : i === search ? styles.taskActive : '';
            return (
              <div className={`${styles.task} ${state}`} key={t}>
                <span className={styles.tk}>
                  {i < search ? (
                    <OrganicIcons.check s={13} />
                  ) : i === search ? (
                    <span className={styles.tkspin} />
                  ) : (
                    i + 1
                  )}
                </span>
                <span>{t}</span>
              </div>
            );
          })}
        </div>
        <div className={styles.sbar}>
          <i className={styles.barFill} style={{ width: `${(search / SEARCH_TASKS.length) * 100}%` }} />
        </div>
      </div>
    );
  } else if (phase === 'results') {
    content = (
      <div className={styles.results}>
        <div className={styles.rhead}>
          <div className={styles.rseal}>
            <OrganicIcons.check s={32} />
          </div>
          <h2 ref={headingRef} tabIndex={-1} className={styles.resultsTitle} style={{ outline: 'none' }}>
            Ottime notizie! Hai diritto a {bonusCount} bonus
          </h2>
          <p>
            Incentivi attivi nel CAP <b>{fields.cap}</b> per abbattere la tua bolletta.
          </p>
        </div>
        <div className={styles.bonus}>
          {INCENTIVES.map((b, i) => (
            <div className={styles.bcard} key={b.t} style={{ animationDelay: `${i * 90}ms` }}>
              <span className={styles.bico}>{renderIcon(b.ico)}</span>
              <span className={styles.bbody}>
                <b>{b.t}</b>
                <small>{b.s}</small>
              </span>
              <span className={styles.btag}>
                <OrganicIcons.check s={11} /> {b.tag}
              </span>
            </div>
          ))}
        </div>
        <div className={styles.rnote}>
          <OrganicIcons.alarm /> Alcuni bonus hanno scadenza: verifica subito la tua idoneità.
        </div>
        <button type="button" className={styles.cta} onClick={goToForm} style={{ marginTop: 14 }}>
          Verifica la tua idoneità{' '}
          <span className={styles.arrow}>
            <OrganicIcons.arrow />
          </span>
        </button>
      </div>
    );
  } else {
    content = <div className={styles.panel}>{body}</div>;
  }

  const showBar = phase === 'form';
  const showTrust = phase === 'form' && step >= 1;
  const fill = ((step + 1) / FORM_STEPS) * 100;
  const headLabel =
    phase === 'results' ? 'I tuoi bonus' : phase === 'searching' ? 'Ricerca in corso…' : '';

  return (
    <section className={styles.sliderSection}>
      <div className={styles.slider} role="region" aria-label="Scopri i bonus per il fotovoltaico">
        <div className={styles.head}>
          <div className={styles.headRow}>
            {phase === 'cap' ? (
              <span className={`${styles.eyebrow} ${styles.eyebrowGreen}`} style={{ margin: 0 }}>
                <OrganicIcons.check s={13} /> Verifica gratuita in 30 secondi
              </span>
            ) : (
              <span className={styles.stepcount}>
                {showBar ? `Passo ${step + 1} di ${FORM_STEPS}` : headLabel}
              </span>
            )}
            <span className={styles.brandmark}>
              Enpal<span className={styles.dot}>.</span>
            </span>
          </div>
          {showBar && (
            <div className={styles.bar}>
              <i className={styles.barFill} style={{ width: `${fill}%` }} />
            </div>
          )}
        </div>

        <div className={styles.viewport}>
          <div
            className={`${styles.phasewrap} ${anim ? (dir === 'next' ? styles.animNext : styles.animPrev) : ''}`}
            key={`${phase}-${step}`}
          >
            {content}
          </div>
        </div>

        {showTrust && (
          <div className={styles.trust}>
            <span>
              <OrganicIcons.users /> 100.000+ clienti in Italia
            </span>
            <span>
              <OrganicIcons.shield /> 0€ anticipo
            </span>
            <span>
              <OrganicIcons.card /> Finanziamento
            </span>
          </div>
        )}
      </div>
    </section>
  );
};

export default SliderOrganic;
